// Composant réutilisable Modal Ticket
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, ChevronDown, ArrowLeft, Sparkles } from 'lucide-react';

// Catégories par défaut pour acheteur → vendeur
const BUYER_CATEGORIES = [
  { id: 'question_produit', label: 'Question sur un produit', icon: '❓' },
  { id: 'disponibilite', label: 'Disponibilité / Stock', icon: '📦' },
  { id: 'livraison', label: 'Livraison / Expédition', icon: '🚚' },
  { id: 'negociation', label: 'Négociation de prix', icon: '💰' },
  { id: 'autre', label: 'Autre demande', icon: '✉️' },
];

// Catégories pour vendeur → acheteur
const SELLER_CATEGORIES = [
  { id: 'suivi_commande', label: 'Suivi de commande', icon: '📦' },
  { id: 'livraison', label: 'Livraison / Expédition', icon: '🚚' },
  { id: 'retour', label: 'Retour / Remboursement', icon: '↩️' },
  { id: 'autre', label: 'Autre message', icon: '✉️' },
];

export function TicketModal({
  isOpen,
  onClose,
  recipientName,
  recipientId,
  type = 'buyer',
  onSuccess
}) {
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const categories = type === 'seller' ? SELLER_CATEGORIES : BUYER_CATEGORIES;
  const labelType = type === 'seller' ? 'message' : 'demande';
  const selectedCategory = categories.find(c => c.id === category);

  useEffect(() => {
    if (!isOpen || !isMobile) { setKeyboardHeight(0); return; }
    const viewport = window.visualViewport;
    if (!viewport) return;
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    return () => {
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async () => {
    if (!category || !subject.trim() || !message.trim()) return;
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/tickets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_id: recipientId || 'unknown',
          seller_name: recipientName || 'Destinataire',
          category,
          subject,
          message,
        })
      });
      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          resetForm();
          onClose();
          onSuccess?.();
        }, 2000);
      } else {
        alert('Erreur lors de la création du ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCategory('');
    setSubject('');
    setMessage('');
    setShowCategoryDropdown(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  // Écran succès commun
  if (showSuccess) {
    return createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowSuccess(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {type === 'seller' ? 'Message envoyé !' : 'Demande envoyée !'}
            </h3>
            <p className="text-gray-500 mb-6">
              {type === 'seller'
                ? 'L\'acheteur a reçu ton message et pourra te répondre rapidement.'
                : 'Le vendeur a reçu ta demande et te répondra dans les meilleurs délais.'}
            </p>
            <button
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold"
              onClick={() => setShowSuccess(false)}
            >
              Compris
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  }

  const formContent = (
    <div className={isMobile ? 'flex-1 overflow-y-auto p-4 space-y-4' : 'p-4 space-y-4 max-h-[60vh] overflow-y-auto'}>
      {/* Catégorie */}
      <div className="relative">
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Sujet de la {labelType} *
        </label>
        <button
          type="button"
          onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
          className="w-full p-4 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <span className={`text-[16px] ${selectedCategory ? 'text-gray-700' : 'text-gray-400'}`}>
            {selectedCategory ? (
              <span className="flex items-center gap-2 text-[15px]">
                <span>{selectedCategory.icon}</span>
                {selectedCategory.label}
              </span>
            ) : 'Sélectionner un sujet'}
          </span>
          <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {showCategoryDropdown && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
            >
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => { setCategory(cat.id); setShowCategoryDropdown(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                    category === cat.id ? 'bg-orange-50 text-orange-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-base">{cat.icon}</span>
                  <span className="font-medium">{cat.label}</span>
                  {category === cat.id && <CheckCircle size={14} className="ml-auto text-orange-500" />}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Sujet */}
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Objet *
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={type === 'seller' ? 'Ex: Information sur l\'expédition' : 'Ex: Question sur la livraison'}
          className="w-full p-4 rounded-xl border border-gray-200 bg-gray-50 text-[16px] focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {/* Message */}
      <div>
        <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
          Ton message *
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={type === 'seller' ? 'Écris ton message à l\'acheteur...' : 'Décris ta demande en détail...'}
          className="w-full p-4 rounded-xl border border-gray-200 bg-gray-50 text-[15px] resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
          rows={3}
        />
      </div>

      <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
        <p className="font-medium mb-0.5">💡 Bon à savoir</p>
        <p>
          {type === 'seller'
            ? 'L\'acheteur recevra ton message et pourra te répondre directement via le système de tickets.'
            : 'Le vendeur recevra ta demande et te répondra dans les meilleurs délais. Tu seras notifié de sa réponse.'}
        </p>
      </div>
    </div>
  );

  if (isMobile) {
    return createPortal(
      <AnimatePresence>
        <motion.div
          className="fixed inset-0 z-[10002] flex flex-col bg-white"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-4 border-b border-gray-100 bg-white"
            style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
          >
            <button onClick={handleClose} className="p-2 -ml-2 text-gray-500">
              <ArrowLeft size={22} />
            </button>
            <h1 className="text-[15px] font-bold text-gray-900 flex-1">
              Envoyer un ticket à {recipientName}
            </h1>
          </div>

          {formContent}

          {/* Footer */}
          <div
            className="p-4 border-t border-gray-100 bg-white"
            style={{
              paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
              marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px',
            }}
          >
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={!category || !subject.trim() || !message.trim() || loading}
                className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : type === 'seller' ? 'Envoyer le message' : 'Envoyer la demande'}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/60 p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-600">
                {recipientName?.charAt(0) || 'D'}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Envoyer un ticket</h3>
                <p className="text-sm text-gray-500">à {recipientName}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {formContent}

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-gray-100">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex-1 h-12 rounded-xl font-semibold border border-gray-300 text-[#1e3a5f]"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!category || !subject.trim() || !message.trim() || loading}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Sparkles size={16} />
                  {type === 'seller' ? 'Envoyer le message' : 'Envoyer la demande'}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

export default TicketModal;
